import { useState, useEffect, useCallback } from "react";
import {
	Plus,
	Pencil,
	Trash2,
	ChevronUp,
	ChevronDown,
	Loader2,
	Save,
	ToggleLeft,
	ToggleRight,
	Globe,
	Layers,
	Eye,
	EyeOff,
	Facebook,
	Linkedin,
	Twitter,
	Instagram,
	Youtube,
	Mail,
} from "lucide-react";
import {
	getFooterConfig,
	saveFooterConfig,
	getAllNavGroups,
	type FooterConfig,
	type FooterSocialLink,
	type NavGroup,
} from "@/lib/cms";

interface AdminFooterConfigProps {
	currentUserId: string;
	showNotify?: (message: string, type?: "success" | "error" | "info") => void;
	/** Optional action (e.g. Help & guides) to show in the title row. */
	titleAction?: React.ReactNode;
}

type SocialPlatform = FooterSocialLink["platform"];

/** TikTok icon for admin (outline style). Lucide does not provide a TikTok icon. */
function TikTokIconAdmin({ size = 16 }: { size?: number }) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
			<path d="M9 12a4 4 0 0 0 4 4h0a4 4 0 0 0 4-4V4" />
			<path d="M17 2h-2a3 3 0 0 0-3 3v2" />
		</svg>
	);
}

const SOCIAL_PLATFORMS: {
	value: SocialPlatform;
	label: string;
	icon: React.ReactNode;
}[] = [
	{ value: "tiktok", label: "TikTok", icon: <TikTokIconAdmin size={16} /> },
	{ value: "facebook", label: "Facebook", icon: <Facebook size={16} /> },
	{ value: "linkedin", label: "LinkedIn", icon: <Linkedin size={16} /> },
	{ value: "twitter", label: "Twitter / X", icon: <Twitter size={16} /> },
	{ value: "instagram", label: "Instagram", icon: <Instagram size={16} /> },
	{ value: "youtube", label: "YouTube", icon: <Youtube size={16} /> },
];

const newSocialId = () =>
	`social_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const emptySocial = (): FooterSocialLink => ({
	id: newSocialId(),
	platform: "facebook",
	url: "",
	isVisible: true,
});

export const AdminFooterConfig = ({
	currentUserId,
	showNotify,
	titleAction,
}: AdminFooterConfigProps) => {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	// Footer groups available for columns
	const [footerGroups, setFooterGroups] = useState<NavGroup[]>([]);

	// Form state
	const [showLeadCaptureBanner, setShowLeadCaptureBanner] = useState(true);
	const [showWebsiteCTABand, setShowWebsiteCTABand] = useState(true);
	const [showNewsletterSection, setShowNewsletterSection] = useState(false);
	const [newsletterHeading, setNewsletterHeading] = useState("");
	const [newsletterDescription, setNewsletterDescription] = useState("");
	const [copyrightText, setCopyrightText] = useState("");
	const [brandDescription, setBrandDescription] = useState("");
	const [socialLinks, setSocialLinks] = useState<FooterSocialLink[]>([]);
	const [footerNavGroupIds, setFooterNavGroupIds] = useState<string[]>([]);

	// Social link editing
	const [editingSocial, setEditingSocial] = useState<FooterSocialLink | null>(
		null,
	);
	const [socialFormOpen, setSocialFormOpen] = useState(false);
	const [socialForm, setSocialForm] = useState<FooterSocialLink>(emptySocial());

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [cfg, groups] = await Promise.all([
				getFooterConfig(),
				getAllNavGroups(),
			]);
			const fGroups = groups.filter((g) => g.placement === "footer");
			setFooterGroups(fGroups);
			if (cfg) {
				setShowLeadCaptureBanner(cfg.showLeadCaptureBanner);
				setShowWebsiteCTABand(cfg.showWebsiteCTABand);
				setShowNewsletterSection(cfg.showNewsletterSection === true);
				setNewsletterHeading(cfg.newsletterHeading ?? "");
				setNewsletterDescription(cfg.newsletterDescription ?? "");
				setCopyrightText(cfg.copyrightText ?? "");
				setBrandDescription(cfg.brandDescription ?? "");
				setSocialLinks([...cfg.socialLinks]);
				// Only keep IDs that exist in current footer groups
				const validIds = cfg.footerNavGroupIds.filter((id) =>
					fGroups.some((g) => g.id === id),
				);
				// Add any footer groups not yet in the list (append at end)
				const remaining = fGroups
					.filter((g) => !validIds.includes(g.id))
					.map((g) => g.id);
				setFooterNavGroupIds([...validIds, ...remaining]);
			} else {
				// Default: add all footer groups
				setFooterNavGroupIds(fGroups.map((g) => g.id));
			}
		} catch (err) {
			console.error("[AdminFooterConfig] Failed to load footer config:", err);
			showNotify?.("Failed to load footer configuration", "error");
		} finally {
			setLoading(false);
		}
	}, [showNotify]);

	useEffect(() => {
		load();
	}, [load]);

	const handleSave = async () => {
		setSaving(true);
		try {
			const cfg: Omit<FooterConfig, "updatedAt" | "updatedBy"> = {
				showLeadCaptureBanner,
				showWebsiteCTABand,
				showNewsletterSection,
				newsletterHeading: newsletterHeading.trim() || null,
				newsletterDescription: newsletterDescription.trim() || null,
				copyrightText: copyrightText.trim() || null,
				brandDescription: brandDescription.trim() || null,
				socialLinks,
				footerNavGroupIds,
			};
			await saveFooterConfig(cfg, currentUserId);
			showNotify?.("Footer configuration saved", "success");
		} catch (err) {
			console.error("[AdminFooterConfig] Failed to save footer config:", err);
			showNotify?.("Failed to save footer configuration", "error");
		} finally {
			setSaving(false);
		}
	};

	// ── Footer column ordering ─────────────────────────────────────────────────
	const moveColumn = (id: string, direction: "up" | "down") => {
		setFooterNavGroupIds((prev) => {
			const idx = prev.indexOf(id);
			const swapIdx = direction === "up" ? idx - 1 : idx + 1;
			if (swapIdx < 0 || swapIdx >= prev.length) return prev;
			const next = [...prev];
			[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
			return next;
		});
	};

	const toggleColumnInFooter = (id: string) => {
		setFooterNavGroupIds((prev) =>
			prev.includes(id) ? prev.filter((gid) => gid !== id) : [...prev, id],
		);
	};

	// ── Social links ──────────────────────────────────────────────────────────
	const openAddSocial = () => {
		setEditingSocial(null);
		setSocialForm(emptySocial());
		setSocialFormOpen(true);
	};

	const openEditSocial = (s: FooterSocialLink) => {
		setEditingSocial(s);
		setSocialForm({ ...s });
		setSocialFormOpen(true);
	};

	const handleSocialSave = () => {
		if (!socialForm.url.trim()) {
			showNotify?.("URL is required", "error");
			return;
		}
		if (editingSocial) {
			setSocialLinks((prev) =>
				prev.map((s) => (s.id === editingSocial.id ? { ...socialForm } : s)),
			);
		} else {
			setSocialLinks((prev) => [...prev, { ...socialForm, id: newSocialId() }]);
		}
		setSocialFormOpen(false);
	};

	const deleteSocial = (id: string) => {
		setSocialLinks((prev) => prev.filter((s) => s.id !== id));
	};

	const moveSocial = (id: string, direction: "up" | "down") => {
		setSocialLinks((prev) => {
			const idx = prev.findIndex((s) => s.id === id);
			const swapIdx = direction === "up" ? idx - 1 : idx + 1;
			if (swapIdx < 0 || swapIdx >= prev.length) return prev;
			const next = [...prev];
			[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
			return next;
		});
	};

	const toggleSocialVisibility = (id: string) => {
		setSocialLinks((prev) =>
			prev.map((s) => (s.id === id ? { ...s, isVisible: !s.isVisible } : s)),
		);
	};

	const Toggle = ({
		checked,
		onChange,
		label,
	}: {
		checked: boolean;
		onChange: () => void;
		label: string;
	}) => (
		<button
			type="button"
			onClick={onChange}
			className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${checked ? "border-blue-300 bg-blue-50 text-blue-700" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}`}
		>
			{checked ? (
				<ToggleRight size={18} className="text-blue-600" />
			) : (
				<ToggleLeft size={18} className="text-gray-400" />
			)}
			{label}
		</button>
	);

	const getPlatformIcon = (platform: SocialPlatform) => {
		const found = SOCIAL_PLATFORMS.find((p) => p.value === platform);
		return found?.icon ?? <Globe size={16} />;
	};

	const getPlatformLabel = (platform: SocialPlatform) => {
		const found = SOCIAL_PLATFORMS.find((p) => p.value === platform);
		return found?.label ?? platform;
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center py-20">
				<Loader2 size={32} className="animate-spin text-blue-500" />
			</div>
		);
	}

	return (
		<div className="space-y-6 max-w-3xl">
			{/* Page header */}
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">
						Footer Configuration
					</h1>
					<p className="text-sm text-gray-500 mt-0.5">
						Manage footer sections, columns, social links, and copyright text.
					</p>
				</div>
				<div className="flex items-center gap-2 shrink-0">
					{titleAction}
					<button
						type="button"
						onClick={handleSave}
						disabled={saving}
						className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-60 shadow-sm"
					>
						{saving ? (
							<Loader2 size={16} className="animate-spin" />
						) : (
							<Save size={16} />
						)}
						Save
					</button>
				</div>
			</div>

			{/* Section visibility */}
			<section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
				<h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
					<ToggleRight size={16} className="text-blue-500" />
					Section Visibility
				</h2>
			<div className="flex flex-wrap gap-3">
				<Toggle
					checked={showLeadCaptureBanner}
					onChange={() => setShowLeadCaptureBanner((v) => !v)}
					label="Federal Readiness Scan banner"
				/>
				<Toggle
					checked={showWebsiteCTABand}
					onChange={() => setShowWebsiteCTABand((v) => !v)}
					label="Website Help CTA band"
				/>
				<Toggle
					checked={showNewsletterSection}
					onChange={() => setShowNewsletterSection((v) => !v)}
					label="Newsletter subscription band"
				/>
			</div>
		</section>

		{/* Newsletter section */}
		<section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
			<h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
				<Mail size={16} className="text-blue-500" />
				Newsletter Section
			</h2>
			<p className="text-xs text-gray-500 -mt-2">
				Controls the heading and description shown in the newsletter subscription band. Enable the band above to show it on the site.
			</p>
			<div>
				<label className="block text-sm font-medium text-gray-700 mb-1">
					Heading
				</label>
				<input
					type="text"
					value={newsletterHeading}
					onChange={(e) => setNewsletterHeading(e.target.value)}
					placeholder="Stay in the loop"
					className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
				/>
				<p className="text-xs text-gray-500 mt-1">Leave blank to use the default heading.</p>
			</div>
			<div>
				<label className="block text-sm font-medium text-gray-700 mb-1">
					Description
				</label>
				<textarea
					rows={2}
					value={newsletterDescription}
					onChange={(e) => setNewsletterDescription(e.target.value)}
					placeholder="Get government contracting insights, tips, and updates delivered to your inbox."
					className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
				/>
				<p className="text-xs text-gray-500 mt-1">Leave blank to use the default description.</p>
			</div>
		</section>

			{/* Brand */}
			<section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
				<h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
					<Layers size={16} className="text-blue-500" />
					Brand &amp; Copyright
				</h2>
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Brand description
					</label>
					<textarea
						rows={2}
						value={brandDescription}
						onChange={(e) => setBrandDescription(e.target.value)}
						placeholder="SMBPrime helps small businesses navigate government contracting."
						className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
					<p className="text-xs text-gray-500 mt-1">
						Short tagline shown below the logo in the footer brand column.
					</p>
				</div>
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Copyright text
					</label>
					<textarea
						rows={2}
						value={copyrightText}
						onChange={(e) => setCopyrightText(e.target.value)}
						placeholder="© 2026 SMBPrime.ai. All rights reserved."
						className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
					<p className="text-xs text-gray-500 mt-1">
						Shown in the bottom copyright bar. Leave blank for default.
					</p>
				</div>
			</section>

			{/* Social links */}
			<section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
				<div className="flex items-center justify-between">
					<h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
						<Globe size={16} className="text-blue-500" />
						Social Links
					</h2>
					<button
						type="button"
						onClick={openAddSocial}
						className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
					>
						<Plus size={14} />
						Add link
					</button>
				</div>
				{socialLinks.length === 0 ? (
					<p className="text-sm text-gray-400 text-center py-6">
						No social links yet.
					</p>
				) : (
					<div className="space-y-2">
						{socialLinks.map((s, idx) => (
							<div
								key={s.id}
								className={`flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50 ${!s.isVisible ? "opacity-50" : ""}`}
							>
								<span className="text-gray-500">
									{getPlatformIcon(s.platform)}
								</span>
								<div className="flex-1 min-w-0">
									<span className="text-sm font-medium text-gray-800">
										{getPlatformLabel(s.platform)}
									</span>
									<span className="text-xs text-gray-500 block truncate">
										{s.url}
									</span>
								</div>
								<div className="flex items-center gap-1 shrink-0">
									<button
										type="button"
										onClick={() => moveSocial(s.id, "up")}
										disabled={idx === 0}
										className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-30"
									>
										<ChevronUp size={14} />
									</button>
									<button
										type="button"
										onClick={() => moveSocial(s.id, "down")}
										disabled={idx === socialLinks.length - 1}
										className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-30"
									>
										<ChevronDown size={14} />
									</button>
									<button
										type="button"
										onClick={() => toggleSocialVisibility(s.id)}
										className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200"
										title={s.isVisible ? "Hide" : "Show"}
									>
										{s.isVisible ? <Eye size={14} /> : <EyeOff size={14} />}
									</button>
									<button
										type="button"
										onClick={() => openEditSocial(s)}
										className="p-1 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50"
									>
										<Pencil size={14} />
									</button>
									<button
										type="button"
										onClick={() => deleteSocial(s.id)}
										className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
									>
										<Trash2 size={14} />
									</button>
								</div>
							</div>
						))}
					</div>
				)}
			</section>

			{/* Footer columns (nav groups) */}
			<section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
				<h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
					<Layers size={16} className="text-blue-500" />
					Footer Columns
				</h2>
				<p className="text-sm text-gray-500">
					Select and order which navigation groups appear as footer columns. Go
					to <strong>Navigation Manager</strong> to create footer groups first.
				</p>
				{footerGroups.length === 0 ? (
					<p className="text-sm text-gray-400 text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-300">
						No footer navigation groups found. Create footer groups in
						Navigation Manager → Footer tab.
					</p>
				) : (
					<div className="space-y-2">
						{footerNavGroupIds
							.map((id) => footerGroups.find((g) => g.id === id))
							.filter((g): g is NavGroup => !!g)
							.concat(
								footerGroups.filter((g) => !footerNavGroupIds.includes(g.id)),
							)
							.map((group) => {
								const included = footerNavGroupIds.includes(group.id);
								return (
									<div
										key={group.id}
										className={`flex items-center gap-2 p-3 rounded-lg border border-gray-200 ${included ? "bg-blue-50 border-blue-200" : "bg-gray-50 opacity-60"}`}
									>
										<input
											type="checkbox"
											checked={included}
											onChange={() => toggleColumnInFooter(group.id)}
											className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
										/>
										<div className="flex-1 min-w-0">
											<span className="text-sm font-medium text-gray-800">
												{group.name}
											</span>
											{group.description && (
												<span className="text-xs text-gray-500 block">
													{group.description}
												</span>
											)}
										</div>
										{included && (
											<div className="flex items-center gap-1 shrink-0">
												<button
													type="button"
													onClick={() => moveColumn(group.id, "up")}
													disabled={footerNavGroupIds.indexOf(group.id) === 0}
													className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-30"
												>
													<ChevronUp size={14} />
												</button>
												<button
													type="button"
													onClick={() => moveColumn(group.id, "down")}
													disabled={
														footerNavGroupIds.indexOf(group.id) ===
														footerNavGroupIds.length - 1
													}
													className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-30"
												>
													<ChevronDown size={14} />
												</button>
											</div>
										)}
										<span
											className={`text-xs px-2 py-0.5 rounded ${included ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-500"}`}
										>
											{included
												? `Col ${footerNavGroupIds.indexOf(group.id) + 1}`
												: "Excluded"}
										</span>
									</div>
								);
							})}
					</div>
				)}
			</section>

			{/* Save bar */}
			<div className="flex justify-end">
				<button
					type="button"
					onClick={handleSave}
					disabled={saving}
					className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-60 shadow-sm"
				>
					{saving ? (
						<Loader2 size={16} className="animate-spin" />
					) : (
						<Save size={16} />
					)}
					Save footer configuration
				</button>
			</div>

			{/* Social form modal */}
			{socialFormOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
					<div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
						<div className="bg-blue-600 px-5 py-4">
							<h3 className="text-base font-bold text-white">
								{editingSocial ? "Edit Social Link" : "Add Social Link"}
							</h3>
						</div>
						<div className="p-5 space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Platform
								</label>
								<select
									value={socialForm.platform}
									onChange={(e) =>
										setSocialForm((f) => ({
											...f,
											platform: e.target.value as SocialPlatform,
										}))
									}
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
								>
									{SOCIAL_PLATFORMS.map((p) => (
										<option key={p.value} value={p.value}>
											{p.label}
										</option>
									))}
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									URL *
								</label>
								<input
									type="text"
									value={socialForm.url}
									onChange={(e) =>
										setSocialForm((f) => ({ ...f, url: e.target.value }))
									}
									placeholder="https://..."
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
								/>
							</div>
							<div className="flex items-center gap-2">
								<input
									id="social-visible"
									type="checkbox"
									checked={socialForm.isVisible}
									onChange={(e) =>
										setSocialForm((f) => ({
											...f,
											isVisible: e.target.checked,
										}))
									}
									className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
								/>
								<label
									htmlFor="social-visible"
									className="text-sm text-gray-700"
								>
									Visible on site
								</label>
							</div>
						</div>
						<div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
							<button
								type="button"
								onClick={() => setSocialFormOpen(false)}
								className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleSocialSave}
								className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
							>
								{editingSocial ? "Save changes" : "Add link"}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
